import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";
import { ChangePasswordForm } from "modules/AccountSettings/components/ChangePasswordModal/components";
import { useChangePasswordMutation, usePasswordStrengthMutation } from "modules/AccountSettings/components/ChangePasswordModal/hooks";
import { PASSWORD_STRENGTH_THRESHOLD } from "modules/Login/components";

import type { ChangePasswordPayload } from "modules/AccountSettings/components/ChangePasswordModal/hooks";

type ChangePasswordModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const FormHelper = createFormHelper<ChangePasswordPayload>();

export const ChangePasswordModal = ({ isOpen, onClose }: ChangePasswordModalProps) => {
  const { t } = useTranslation();

  const [errorMsg, setErrorMsg] = useState("");

  const { strength, checkPasswordStrength, resetPasswordStrength } = usePasswordStrengthMutation();
  const { mutateAsync: changePassword, isPending: isChangePasswordPending } = useChangePasswordMutation();

  const isFormInvalid = ({ new_password, new_password_confirm, old_password }: ChangePasswordPayload) => {
    let msg = "";

    if (!old_password || !new_password || !new_password_confirm)
      msg = t("v8_please_check_your_input_fill_in_all_the_required_fields", "Please check your input. Fill in all the required fields.");
    else if (new_password !== new_password_confirm)
      msg = t("v8_new_password_and_new_password_confirmation_must_be_the_same", "New password and new password confirmation must be the same.");
    else if (old_password === new_password_confirm)
      msg = t("v8_new_password_must_be_different_from_your_previous_password", "New password must be different from your previous password");
    else if (!strength || strength < PASSWORD_STRENGTH_THRESHOLD) msg = t("v8_password_not_secure_enough", "New password is not secure enough.");

    setErrorMsg(msg);
    return msg.length > 0;
  };

  const handleClose = () => {
    setErrorMsg("");
    resetPasswordStrength();
    onClose();
  };

  const handleSubmit = async (values: ChangePasswordPayload) => {
    if (isFormInvalid(values)) return;
    await changePassword(values);
    handleClose();
  };

  return (
    <Modal size="tiny" open={isOpen} className="ChangePasswordModal" onClose={handleClose} closeOnDimmerClick={false}>
      <FormHelper.Form defaultValues={{ new_password: "", new_password_confirm: "", old_password: "" }} onSubmit={handleSubmit}>
        <ChangePasswordForm
          hasErrors={errorMsg.length > 0}
          strength={strength}
          errorMsg={errorMsg}
          isChangePasswordPending={isChangePasswordPending}
          onClose={handleClose}
          onSubmit={handleSubmit}
          onResetPasswordStrength={resetPasswordStrength}
          onResetErrorMsg={() => setErrorMsg("")}
          checkPasswordStrength={checkPasswordStrength}
        />
      </FormHelper.Form>
    </Modal>
  );
};
